import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';

import { LoadingButton } from '@mui/lab';

import { EventReport, Incident, IncidentCreateResponse } from '../../core/api/clients';
import useFindIncident from '../../core/api/hooks/incident/useFindIncident';
import useCreateNewIncident from '../../core/api/hooks/incident/useCreateNewIncident';
import { useAuthorizedFormData } from '../../core/context/AuthorizedFormDataContext';
import useAddMultipleToIncident from '../../core/api/hooks/event-report/useAddMultipleToIncident';

interface CreateIncidentButtonProps {
  eventReports: EventReport[];
}

function CreateIncidentButton({ eventReports }: CreateIncidentButtonProps) {
  const navigate = useNavigate();

  const [isCreatingIncident, setIsCreatingIncident] = React.useState<boolean>(false);
  const [foundIncident, setFoundIncident] = React.useState<Incident | undefined>(undefined);
  const [incidentWarningDialogOpen, setIncidentWarningDialogOpen] = React.useState<boolean>(false);

  const { metadata } = useAuthorizedFormData();

  const { mutate: linkEventReports, status: linkingStatus } = useAddMultipleToIncident();
  const { mutate: createNewIncident } = useCreateNewIncident();
  const { refetch: refetchFindIncident } = useFindIncident({
    flightNumber: eventReports[0]?.flightNumber ?? '',
    eventDate: eventReports[0]?.eventDate ?? '',
    aircraftNumber: eventReports[0]?.aircraftNumber ?? '',
  });

  const handleCreateIncident = () => {
    createNewIncident(eventReports.map((eventReport) => ({
      incidentId: undefined,
      eventReportId: eventReport.id,
      incidentConcurrencyId: undefined,
      eventReportConcurrencyId: eventReport.concurrencyId,
    })), {
      onSuccess: (response: IncidentCreateResponse) => {
        if (response.success) {
          if (response.incidentDto) {
            navigate(`${metadata?.route}/${response.incidentDto.eventReport?.id}/edit`, {
              replace: true,
            });
          }
        }

        // TODO: This isn't working right now, but we should make an effort to not send unsuccessful
        //  responses as success (200 OK). Try to handle this in the onError event.
        // setShowIncidentCreateDialog(!response.success);
        // setIncidentCreateIds(response.incidentIds ?? []);
      },
    });
  };

  const handleBeginCreateIncident = () => {
    setIsCreatingIncident(true);

    refetchFindIncident().then((({ data }) => {
      if (data !== undefined && data !== null) {
        setFoundIncident(data);
        setIncidentWarningDialogOpen(true);
      } else {
        handleCreateIncident();
        setIsCreatingIncident(false);
      }
    }));
  };

  const handleContinueClick = () => handleCreateIncident();

  const handleCancelClick = () => {
    setIncidentWarningDialogOpen(false);
    setIsCreatingIncident(false);
  };

  const handleLinkToIncident = () => {
    linkEventReports(eventReports.map((report) => (
      {
        incidentId: foundIncident?.id,
        eventReportId: report.id,
        incidentConcurrencyId: foundIncident?.concurrencyId,
        eventReportConcurrencyId: report.concurrencyId,
      }
    )), {
      onSuccess: () => {
        navigate(`${metadata?.route}/${foundIncident?.id}/edit`, {
          replace: true,
        });
      },
    });
  };

  return (
    <React.Fragment>
      <LoadingButton
        loading={isCreatingIncident}
        onClick={handleBeginCreateIncident}
      >
        Create Incident
      </LoadingButton>
      <Dialog open={incidentWarningDialogOpen}>
        <DialogTitle>Incident Exists</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`An incident with the same Flight Number, Event Date and Aircraft Number
            already exists. Link to existing incident or create a new incident?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button disabled={linkingStatus === 'pending'} onClick={handleLinkToIncident}>Link To Incident</Button>
          <Button disabled={linkingStatus === 'pending'} onClick={handleCancelClick}>Cancel</Button>
          <Button disabled={linkingStatus === 'pending'} onClick={handleContinueClick}>Create New Incident</Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}

export default CreateIncidentButton;
